import { useMemo, useState } from "react";
import { Paper } from "@/types/paper";
import PaperCard from "@/components/papers/PaperCard";
import { AlertCircle, BookOpen, ArrowUpDown } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

interface AuthorPapersListProps {
  papers: Paper[];
  authorName: string;
}

type SortMode = "newest" | "oldest";

const AuthorPapersList = ({ papers, authorName }: AuthorPapersListProps) => {
  const [sort, setSort] = useState<SortMode>("newest");

  const sorted = useMemo(() => {
    const copy = [...papers];
    copy.sort((a, b) => {
      const at = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
      const bt = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
      return sort === "newest" ? bt - at : at - bt;
    });
    return copy;
  }, [papers, sort]);

  return (
    <section
      aria-label={`Publications by ${authorName}`}
      className="rounded-3xl border border-border/60 bg-card shadow-sm overflow-hidden"
    >
      <header className="flex flex-wrap items-end justify-between gap-3 px-6 md:px-10 pt-8 pb-4 border-b border-border/50">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
            Catalogue
          </p>
          <h2 className="mt-1 text-2xl font-semibold tracking-tight text-foreground">
            Publications
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {papers.length === 0
              ? `No publications attributed to ${authorName} yet.`
              : `${papers.length} ${papers.length === 1 ? "paper" : "papers"} authored or co-authored.`}
          </p>
        </div>

        {papers.length > 1 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSort((s) => (s === "newest" ? "oldest" : "newest"))}
            className="gap-1.5 text-muted-foreground hover:text-foreground"
          >
            <ArrowUpDown className="h-3.5 w-3.5" />
            {sort === "newest" ? "Newest first" : "Oldest first"}
          </Button>
        )}
      </header>

      <div className="px-6 md:px-10 py-6">
        {papers.length === 0 ? (
          <div className="flex flex-col items-center text-center py-10">
            <div className="relative mb-4">
              <div
                aria-hidden
                className="absolute -inset-3 rounded-full blur-xl opacity-40"
                style={{ background: "hsl(var(--primary) / 0.25)" }}
              />
              <BookOpen className="relative h-10 w-10 text-muted-foreground" />
            </div>
            <p className="text-foreground font-medium">No publications yet</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-md">
              Papers appear here once the researcher uploads or claims authorship.
            </p>
            <Alert className="mt-6 max-w-md text-left">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>How authorship works</AlertTitle>
              <AlertDescription>
                Papers are attributed automatically when the researcher uploads them
                directly or claims authorship on existing papers in the catalogue.
              </AlertDescription>
            </Alert>
          </div>
        ) : (
          <div className="space-y-4">
            {sorted.map((paper) => (
              <PaperCard key={paper.id} paper={paper} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default AuthorPapersList;
